import React, { useState, useEffect, useRef } from "react";
import { Card, Form, Button, Alert, Spinner, Row, Col, Modal, ProgressBar } from "react-bootstrap";
import Cookies from "js-cookie";
import axios from "axios";
import { QrCode, Check, X, Copy, ExternalLink, Download, Clock } from "react-bootstrap-icons";
import { API_BASE_URL as API_BASE } from "../../../config";

function ConfiguracaoMenuia() {
  const [clienteId, setClienteId] = useState(null);
  const [appKey, setAppKey] = useState("");
  const [authKey, setAuthKey] = useState("");
  const [usaApiLembrete, setUsaApiLembrete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeBase64, setQrCodeBase64] = useState("");
  // const [masterAuthKey, setMasterAuthKey] = useState(""); // Removido, chave é buscada no backend
  // const [showAuthKeyInput, setShowAuthKeyInput] = useState(false); // Removido, não é mais necessário
  const [isPolling, setIsPolling] = useState(false);
  const [pollingMessage, setPollingMessage] = useState("");
  const [pollingProgress, setPollingProgress] = useState(0);
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);

  const POLLING_INTERVAL = 6000; // 2 segundos
  const POLLING_TIMEOUT = 300000; // 5 minutos

  // Pega o usuário do cookie de forma segura
  function getUsuarioCookieSafe() {
    try {
      const raw = Cookies.get("usuario");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  // Carrega as configurações do cliente master
  useEffect(() => {
    const fetchConfig = async () => {
      const usuario = getUsuarioCookieSafe();
      if (!usuario || usuario.role !== "ClienteMaster") {
        setError("Acesso negado. Você não é um cliente master.");
        setLoading(false);
        return;
      }

      setClienteId(usuario.id);

      try {
        const token = Cookies.get("authToken");
        const response = await axios.get(`${API_BASE}/ClienteMaster/${usuario.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data || {};

        setAppKey(data.appKey || "");
        setAuthKey(data.authKey || "");
        setUsaApiLembrete(!!data.usaApiLembrete);
      } catch (err) {
        console.error("Erro ao buscar configurações:", err);
        setError("Erro ao carregar configurações.");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Função para verificar se o dispositivo foi conectado (validação ativa)
const checkForUpdatedKeys = async () => {
  try {
    const token = Cookies.get("authToken");
    
    // Chama o endpoint de verificação que usa a API do Menuia
    const response = await axios.post(
      `${API_BASE}/ClienteMaster/verificar-dispositivo/${clienteId}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const data = response.data || {};

    // Se o dispositivo não está conectado, continua polling
    if (!data.connected) {
      return false;
    }

    // Se está conectado e tem as chaves, para o polling && data.appKey && data.authKey
    if (data.connected) {
      stopPolling();

      setAppKey(data.appKey);
      setAuthKey(data.authKey);
      setUsaApiLembrete(true);

      setSuccess(true);
      setPollingMessage("✅ Dispositivo conectado!");

      // Fecha após 3s
      setTimeout(() => {
        setShowQrModal(false);
        setPollingMessage("");
      }, 3000);

      return true;
    }

    return false;

  } catch (err) {
    console.error("Erro ao verificar dispositivo:", err);
    return false;
  }
};

  const startPolling = () => {
  if (isPolling) return; // evita duplicação

  setIsPolling(true);
  setPollingProgress(0);
  setPollingMessage("⏳ Aguardando conexão do dispositivo...");

  let elapsedTime = 0;

  pollingIntervalRef.current = setInterval(async () => {
    elapsedTime += POLLING_INTERVAL;

    const progress = Math.min((elapsedTime / POLLING_TIMEOUT) * 100, 99);
    setPollingProgress(progress);

    const connected = await checkForUpdatedKeys();
    if (connected) return; 

  }, POLLING_INTERVAL);

  pollingTimeoutRef.current = setTimeout(() => {
    stopPolling();
    setPollingMessage("⏱️ Tempo limite atingido. Tente novamente.");
    setError("Tempo limite de espera excedido. Por favor, tente novamente.");
  }, POLLING_TIMEOUT);
};

  // Para o polling
  const stopPolling = () => {
    setIsPolling(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  };

  // Cleanup ao desmontar o componente
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Gerar QR Code
  const handleGerarQrCode = async () => {
    // A chave é buscada automaticamente no backend, não é mais necessário validar aqui.

    setGenerating(true);
    setError(null);
    setSuccess(false);

    try {
      const token = Cookies.get("authToken");
      const response = await axios.post(
        `${API_BASE}/ClienteMaster/gerar-qrcode/${clienteId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.qrCodeBase64) {
        setQrCodeBase64(response.data.qrCodeBase64);
        setShowQrModal(true);
        // setMasterAuthKey(""); // Removido, não é mais necessário
        
        // Inicia o polling após exibir o QR Code
        setTimeout(() => {
          startPolling();
        }, 500);
      } else {
        setError("Erro ao gerar QR Code. Tente novamente.");
      }
    } catch (err) {
      console.error("Erro ao gerar QR Code:", err);
      const errorMessage = err.response?.data?.Message || err.message || "Erro desconhecido ao gerar QR Code.";
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  // Copiar para clipboard
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    window.globalAlert("Copiado para a área de transferência!", "success");
  };

  // Baixar imagem do QR Code
  const handleDownloadQrCode = () => {
    const link = document.createElement("a");
    link.href = qrCodeBase64;
    link.download = "qr-code-menuia.png";
    link.click();
  };

  // Fechar modal e parar polling
  const handleCloseModal = () => {
    stopPolling();
    setShowQrModal(false);
    setPollingMessage("");
    setPollingProgress(0);
  };

  if (loading) return <Spinner animation="border" />;

  const cardStyle = {
    backgroundColor: "#3c4049",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "25px",
    width: "95%",
    maxWidth: "1200px",
    margin: "0 auto",
  };

  return (
    <>
      <Card className="shadow-lg" style={cardStyle}>
        <Card.Title className="mb-4 fs-3 text-center">
          <QrCode size={28} className="me-2" />
          Configuração da API Menuia
        </Card.Title>

        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">Operação realizada com sucesso!</Alert>}

          {/* Status das Chaves */}
          <div className="mb-4">
            <h5>Status da Integração</h5>
            <Row className="mb-3">
              <Col md={6}>
                <div className="p-3" style={{ backgroundColor: "#2a2d32", borderRadius: "8px" }}>
                  <div className="d-flex align-items-center mb-2">
                    {appKey ? (
                      <Check size={20} className="text-success me-2" />
                    ) : (
                      <X size={20} className="text-danger me-2" />
                    )}
                    <strong>App Key</strong>
                  </div>
                  {appKey ? (
                    <div className="d-flex align-items-center">
                      <code style={{ fontSize: "12px", wordBreak: "break-all" }}>
                        {appKey.substring(0, 20)}...
                      </code>
                      <Button
                        variant="sm"
                        className="ms-2"
                        onClick={() => handleCopyToClipboard(appKey)}
                      >
                        <Copy size={14} />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-muted">Não configurada</span>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="p-3" style={{ backgroundColor: "#2a2d32", borderRadius: "8px" }}>
                  <div className="d-flex align-items-center mb-2">
                    {authKey ? (
                      <Check size={20} className="text-success me-2" />
                    ) : (
                      <X size={20} className="text-danger me-2" />
                    )}
                    <strong>Auth Key</strong>
                  </div>
                  {authKey ? (
                    <div className="d-flex align-items-center">
                      <code style={{ fontSize: "12px", wordBreak: "break-all" }}>
                        {authKey.substring(0, 20)}...
                      </code>
                      <Button
                        variant="sm"
                        className="ms-2"
                        onClick={() => handleCopyToClipboard(authKey)}
                      >
                        <Copy size={14} />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-muted">Não configurada</span>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          <hr className="my-4" />

          {/* Gerar QR Code */}
          <div className="mb-4">
            <h5>Gerar QR Code para Conectar Dispositivo</h5>
            <p className="text-muted">
              Escaneie o QR Code com um dispositivo WhatsApp para conectar e obter as chaves de autenticação.
            </p>

<Button
	              variant="primary"
	              onClick={handleGerarQrCode}
	              disabled={generating}
	            >
	              <QrCode size={18} className="me-2" />
	              {generating ? <Spinner animation="border" size="sm" /> : "Gerar QR Code"}
	            </Button>
	          </div>

          <hr className="my-4" />

          {/* Informações Úteis */}
          <div>
            <h5>Informações Úteis</h5>
            <ul className="text-muted">
              <li>
                <strong>O que é AppKey?</strong> Identificador único do seu aplicativo no Menuia.
              </li>
              <li>
                <strong>O que é AuthKey?</strong> Chave de autenticação para fazer requisições à API do Menuia.
              </li>
              <li>
                <strong>Como funciona?</strong> Ao escanear o QR Code com um dispositivo WhatsApp, as chaves serão automaticamente salvas no seu perfil.
              </li>
              <li>
                <strong>Segurança:</strong> Nunca compartilhe suas chaves com terceiros. Elas são sensíveis e permitem acesso à sua conta.
              </li>
            </ul>
          </div>
        </Card.Body>
      </Card>

      {/* Modal para exibir QR Code */}
      <Modal show={showQrModal} onHide={handleCloseModal} centered size="lg" backdrop="static" keyboard={false}>
        <Modal.Header closeButton={!isPolling}>
          <Modal.Title>QR Code - Conectar Dispositivo</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {qrCodeBase64 && (
            <div style={{ marginBottom: "20px" }}>
              <img
                src={qrCodeBase64}
                alt="QR Code"
                style={{ maxWidth: "100%", height: "auto", maxHeight: "400px" }}
              />
            </div>
          )}
          
          <p className="text-muted mb-3">
            Escaneie este QR Code com um dispositivo WhatsApp para conectar e obter as chaves de autenticação.
          </p>

          {/* Status do Polling */}
          {isPolling && (
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Clock size={18} className="me-2 text-info" />
                <span className="text-info">{pollingMessage}</span>
              </div>
              <ProgressBar 
                now={pollingProgress} 
                label={`${Math.round(pollingProgress)}%`}
                className="mb-2"
              />
              <small className="text-muted">
                Tempo limite: 5 minutos
              </small>
            </div>
          )}

          {/* Mensagem de Sucesso */}
          {success && pollingMessage && (
            <Alert variant="success" className="mb-3">
              {pollingMessage}
            </Alert>
          )}

          {/* Botões de ação */}
          <div className="d-flex gap-2 justify-content-center flex-wrap">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleDownloadQrCode}
              disabled={isPolling}
            >
              <Download size={14} className="me-1" />
              Baixar QR Code
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {isPolling ? (
            <Button variant="danger" onClick={handleCloseModal}>
              Cancelar Espera
            </Button>
          ) : (
            <Button variant="secondary" onClick={handleCloseModal}>
              Fechar
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ConfiguracaoMenuia;
